import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Save, Send, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { QuestionCard, isAnswered, type QuestionType, type QuestionOptions } from "@/components/assessments/QuestionCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// VendorAssessmentFill
//
// Screen — /vendor-portal/:token
// External portal: no sidebar, header with org name + progress bar,
// Section nav (300px) + question area, Save Draft + Submit buttons.
// Design: §8.5 Vendor Assessment Fill
// ─────────────────────────────────────────────

interface QuestionRecord {
  id: string;
  code: string;
  text: string;
  helpText?: string;
  type: QuestionType;
  options: QuestionOptions | null;
  sortOrder: number;
}

interface PostureRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  questions: QuestionRecord[];
}

interface DomainRecord {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  postures: PostureRecord[];
}

interface ResponseRecord {
  questionId: string;
  value: unknown;
  comment: string | null;
}

interface VendorPortalAssessmentResponse {
  data: {
    id: string;
    organizationName: string;
    schemaName: string;
    status: string;
    dueDate: string | null;
    domains: DomainRecord[];
    responses: ResponseRecord[];
    totalQuestions: number;
    answeredQuestions: number;
  };
}

type ResponseMap = Map<string, { value: unknown; comment: string }>;

const DEBOUNCE_MS = 1500;

export function VendorAssessmentFill() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Retrieve session token from localStorage — set during PIN verification in VendorLogin
  const sessionToken = token ? (localStorage.getItem(`vendor_session_${token}`) ?? "") : "";
  const sessionHeaders: Record<string, string> = sessionToken
    ? { "x-vendor-session": sessionToken }
    : {};

  const [activeDomainIdx, setActiveDomainIdx] = useState(0);
  const [responses, setResponses] = useState<ResponseMap>(new Map());
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [activeDomainIdx]);
  const pendingRef = useRef<Map<string, { value: unknown; comment: string }>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Serializes saves so two PATCHes never race, and lets submit await the drain.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const { data, isLoading, error } = useQuery<VendorPortalAssessmentResponse>({
    queryKey: ["vendor-portal", token],
    queryFn: () => api.get(`/vendor-portal/${token}`, sessionHeaders),
    enabled: Boolean(token),
    retry: false,
  });

  // Seed response map from API data when it first loads
  useEffect(() => {
    if (data?.data?.responses) {
      const map: ResponseMap = new Map();
      for (const r of data.data.responses) {
        map.set(r.questionId, {
          value: r.value ?? "",
          comment: r.comment ?? "",
        });
      }
      setResponses(map);
    }
  }, [data?.data?.responses]);

  const saveMutation = useMutation({
    mutationFn: (responses: { questionId: string; value: unknown; comment?: string }[]) =>
      api.patch(`/vendor-portal/${token}/responses`, { responses }, sessionHeaders),
    onError: () => toast.error(t("vendorPortal.autoSaveFailed")),
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/vendor-portal/${token}/submit`, {}, sessionHeaders),
    onSuccess: () => {
      setSubmitted(true);
      // Clear session token from localStorage on successful submission
      if (token) localStorage.removeItem(`vendor_session_${token}`);
      toast.success(t("vendorPortal.submitSuccess"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // S-093: keep queued answers until the save SUCCEEDS. On failure they stay in
  // the queue (surfaced via Retry) instead of being cleared before the request,
  // so a transient/slow save never silently drops a vendor's answers.
  async function flushPending(): Promise<void> {
    // One save at a time — submit/save-draft await the in-flight one first.
    if (inFlightRef.current) {
      try { await inFlightRef.current; } catch { /* surfaced via Retry */ }
    }
    const pending = pendingRef.current;
    if (pending.size === 0) return;
    const snapshot = Array.from(pending.entries());
    const payload = snapshot.map(([questionId, { value, comment }]) => ({
      questionId,
      value,
      ...(comment ? { comment } : {}),
    }));
    const run = (async () => {
      await saveMutation.mutateAsync(payload);
      // Success: drop exactly what we saved, unless re-edited mid-flight
      // (handlers store a fresh object, so identity tells them apart).
      for (const [questionId, saved] of snapshot) {
        if (pendingRef.current.get(questionId) === saved) pendingRef.current.delete(questionId);
      }
    })();
    inFlightRef.current = run;
    try {
      await run;
    } finally {
      if (inFlightRef.current === run) inFlightRef.current = null;
    }
  }

  // Always invoke the latest flushPending from stable callbacks (timer/save/submit).
  const flushRef = useRef(flushPending);
  flushRef.current = flushPending;

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flushRef.current().catch(() => {});
    }, DEBOUNCE_MS);
  }, []);

  // S-092 / L-031: seed the field the user did NOT touch from the last-known value
  // (pending → else the saved responses map), never from an empty default. A
  // comment-only change must carry the saved answer, or the server upsert would
  // overwrite the stored answer with "" — silent, unrecoverable data loss.
  function handleValueChange(questionId: string, value: unknown) {
    setResponses((prev) => {
      const next = new Map(prev);
      next.set(questionId, { value, comment: prev.get(questionId)?.comment ?? "" });
      return next;
    });
    const comment =
      pendingRef.current.get(questionId)?.comment ?? responses.get(questionId)?.comment ?? "";
    pendingRef.current.set(questionId, { value, comment });
    scheduleFlush();
  }

  function handleCommentChange(questionId: string, comment: string) {
    setResponses((prev) => {
      const next = new Map(prev);
      next.set(questionId, { value: prev.get(questionId)?.value ?? "", comment });
      return next;
    });
    const value =
      pendingRef.current.get(questionId)?.value ?? responses.get(questionId)?.value ?? "";
    pendingRef.current.set(questionId, { value, comment });
    scheduleFlush();
  }

  async function handleSaveDraft() {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      await flushRef.current();
      toast.success(t("vendorPortal.draftSaved"));
    } catch {
      /* saveMutation.onError already surfaces the failure */
    }
  }

  // L-033: confirm the irreversible submit, and AWAIT the pending save before
  // submitting (no fixed 300ms race) — if the save fails we do NOT submit, so a
  // final answer can never be dropped while the assessment still submits.
  async function doSubmit() {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      await flushRef.current();
    } catch {
      return; // keep the dialog open; save error already toasted
    }
    submitMutation.mutate();
  }

  if (isLoading) return (
    <div className="flex flex-1 items-center justify-center">
      <PageSkeleton />
    </div>
  );

  const assessment = data?.data;
  if (!assessment) {
    // S-087: give a real recovery path instead of a dead end. The common case is a
    // session/token problem (expired short-lived session, link reopened in another
    // browser, revoked access) — send the vendor back to PIN login rather than
    // stranding them. Saved answers are preserved server-side.
    const httpStatus = (error as { status?: number } | null)?.status;
    const expired = httpStatus === 401 || httpStatus === 403;
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-[18px] font-semibold text-text-primary">
          {expired ? t("vendorPortal.sessionExpired") : t("vendorPortal.linkUnavailable")}
        </p>
        <p className="max-w-md text-body text-text-muted">
          {expired
            ? t("vendorPortal.sessionExpiredDesc")
            : t("vendorPortal.linkUnavailableDesc")}
        </p>
        <Button onClick={() => navigate("/vendor-portal/login")}>{t("vendorPortal.returnToLogin")}</Button>
      </div>
    );
  }

  // WP11: once the vendor submits, the assessment moves to UNDER_REVIEW (and only
  // reaches COMPLETED after admin approval). Both are post-submission states, so
  // the portal shows the "submitted" confirmation and never an editable form.
  if (submitted || assessment.status === "COMPLETED" || assessment.status === "UNDER_REVIEW") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <p className="text-[20px] font-semibold text-text-primary">{t("vendorPortal.submitted")}</p>
        <p className="text-body text-text-muted">
          {t("vendorPortal.submittedDesc")}
        </p>
      </div>
    );
  }

  const domains = assessment.domains ?? [];
  const activeDomain = domains[activeDomainIdx];
  // S-102: live answered count from the reactive responses map (plain consts —
  // must stay below the early returns above, so no hooks here).
  const allQuestions = domains.flatMap((d) => d.postures.flatMap((p) => p.questions));
  const answeredCount = allQuestions.filter((q) => isAnswered(q.type, responses.get(q.id)?.value)).length;
  const totalQ = assessment.totalQuestions;
  const answeredQ = answeredCount;
  const progressPct = totalQ > 0 ? Math.round((answeredQ / totalQ) * 100) : 0;
  const canSubmit = answeredCount >= 1;
  const isReadOnly = assessment.status === "COMPLETED" || assessment.status === "UNDER_REVIEW";

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Sub-header with org name + progress */}
      <div
        className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-border-default bg-card px-8 py-3"
      >
        <div>
          <p className="text-[14px] font-medium text-text-primary">{assessment.organizationName}</p>
          <p className="text-caption text-text-muted">{assessment.schemaName}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-hover">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-caption text-text-muted">{answeredQ}/{totalQ}</span>
          </div>
          {/* S-093: surface a failed autosave with a Retry instead of dropping answers. */}
          {saveMutation.isError && !isReadOnly && (
            <div className="flex items-center gap-2 text-[13px]" style={{ color: "var(--danger)" }}>
              <span>{t("vendorPortal.saveFailed")}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void flushRef.current().catch(() => {}); }}
              >
                {t("common.retry")}
              </Button>
            </div>
          )}
          {/* Buttons */}
          {!isReadOnly && (
            <>
              <Button variant="outline" size="sm" onClick={handleSaveDraft} isLoading={saveMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {t("vendorPortal.saveDraft")}
              </Button>
              <Button
                size="sm"
                onClick={() => setSubmitOpen(true)}
                isLoading={submitMutation.isPending}
                disabled={!canSubmit}
              >
                <Send className="mr-1.5 h-3.5 w-3.5" />
                {t("vendorPortal.submit")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Body: section nav + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Section nav */}
        <div
          className="w-[300px] flex-shrink-0 overflow-y-auto border-r border-border-default bg-card p-4"
        >
          <p className="mb-2 text-caption font-medium uppercase tracking-wider text-text-muted">
            {t("vendorPortal.sections")}
          </p>
          <nav className="flex flex-col gap-0.5">
            {domains.map((domain: DomainRecord, idx: number) => {
              const isActive = idx === activeDomainIdx;
              const domainTotal = domain.postures.reduce((s: number, p: PostureRecord) => s + p.questions.length, 0);
              const domainAnswered = domain.postures.reduce(
                (s: number, p: PostureRecord) =>
                  s +
                  p.questions.filter((q: QuestionRecord) => {
                    const v = responses.get(q.id)?.value;
                    if (v === null || v === undefined) return false;
                    if (typeof v === "string") return v.trim() !== "";
                    if (typeof v === "object") return Object.keys(v as object).length > 0;
                    return true;
                  }).length,
                0
              );
              return (
                <button
                  key={domain.id}
                  type="button"
                  onClick={() => setActiveDomainIdx(idx)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-control px-3 py-2 text-left text-body transition-colors",
                    isActive
                      ? "bg-accent-bg text-accent-text"
                      : "text-text-secondary hover:bg-hover hover:text-text-primary"
                  )}
                >
                  <span>{domain.name}</span>
                  <span className={cn("text-caption", isActive ? "text-accent-text" : "text-text-muted")}>
                    {domainAnswered}/{domainTotal}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Question content area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto p-8">
          {activeDomain ? (
            <div className="flex flex-col gap-6">
              <h2 className="text-[20px] font-semibold text-text-primary">{activeDomain.name}</h2>

              {activeDomain.postures.map((posture: PostureRecord) => (
                <div key={posture.id}>
                  <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-text-muted">
                    {posture.code} · {posture.name}
                  </h3>
                  <div className="flex flex-col gap-3">
                    {posture.questions.map((question: QuestionRecord, qIdx: number) => {
                      const resp = responses.get(question.id) ?? { value: "", comment: "" };
                      return (
                        <QuestionCard
                          key={question.id}
                          questionId={question.id}
                          index={qIdx + 1}
                          code={question.code}
                          text={question.text}
                          helpText={question.helpText}
                          type={question.type}
                          options={question.options}
                          value={resp.value}
                          comment={typeof resp.comment === "string" ? resp.comment : null}
                          onValueChange={handleValueChange}
                          onCommentChange={handleCommentChange}
                          readOnly={isReadOnly}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Previous/Next navigation */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setActiveDomainIdx((i) => Math.max(0, i - 1))}
                  disabled={activeDomainIdx === 0}
                >
                  <ChevronLeft className="mr-1.5 h-4 w-4" />
                  {t("vendorPortal.previousSection")}
                </Button>
                <span className="text-caption text-text-muted">
                  {t("vendorPortal.sectionCount", { current: activeDomainIdx + 1, total: domains.length })}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setActiveDomainIdx((i) => Math.min(domains.length - 1, i + 1))}
                  disabled={activeDomainIdx === domains.length - 1}
                >
                  {t("vendorPortal.nextSection")}
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-24 text-text-muted">
              {t("vendorPortal.noSections")}
            </div>
          )}
        </div>
      </div>

      {/* Submit is irreversible and locks the vendor out — confirm first (L-033). */}
      <ConfirmDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        isDestructive={false}
        title={t("vendorPortal.submitConfirmTitle")}
        description={t("vendorPortal.submitConfirmDesc")}
        confirmLabel={t("vendorPortal.submit")}
        isLoading={submitMutation.isPending}
        onConfirm={doSubmit}
      />
    </div>
  );
}
